import type { ErrorCode } from '@complan/contracts';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/shared/api/api-error';
import { describeError, reportError } from './report-error';

/**
 * The one place an error becomes UI. Every failure the app renders is one of the
 * six taxonomy codes, so the mapping from `code` to a message, an action and a
 * severity is a single table rather than scattered `if` branches.
 *
 * Two codes drive navigation rather than a static message:
 * - `unauthenticated` sends the user into the OIDC exchange, retaining the path
 *   they were on so R1.1 can return them to it.
 * - `not_found`, `forbidden`, `validation_failed`, `conflict` and `internal`
 *   render in place; none of them reveals whether a withheld record exists (R2.8,
 *   R3.6), because the shapes themselves carry nothing that could.
 */
interface ErrorPresentationKeys {
  readonly titleKey: string;
  readonly descriptionKey: string;
}

/**
 * The taxonomy-code → catalogue-key table. Only keys live here; the text is read
 * from the externalized catalogue at render (R18.9), so no error message is
 * inlined in source.
 */
const PRESENTATION: Record<ErrorCode, ErrorPresentationKeys> = {
  unauthenticated: {
    titleKey: 'error.unauthenticated.title',
    descriptionKey: 'error.unauthenticated.description',
  },
  forbidden: {
    titleKey: 'error.forbidden.title',
    descriptionKey: 'error.forbidden.description',
  },
  not_found: {
    titleKey: 'error.notFound.title',
    descriptionKey: 'error.notFound.description',
  },
  validation_failed: {
    titleKey: 'error.validationFailed.title',
    descriptionKey: 'error.validationFailed.description',
  },
  conflict: {
    titleKey: 'error.conflict.title',
    descriptionKey: 'error.conflict.description',
  },
  internal: {
    titleKey: 'error.internal.title',
    descriptionKey: 'error.internal.description',
  },
};

export function toErrorCode(error: unknown): ErrorCode {
  if (error instanceof ApiRequestError) {
    return error.code;
  }
  return 'internal';
}

export function correlationIdOf(error: unknown): string | null {
  if (error instanceof ApiRequestError) {
    return error.body.correlationId || null;
  }
  return null;
}

/**
 * Begin the OIDC exchange, retaining the current path and query so the user
 * returns to exactly the view they were on (R1.1).
 */
export function beginLogin(): void {
  const target = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/login?target=${encodeURIComponent(target)}`);
}

export interface ErrorContentProps {
  readonly error: unknown;
  /**
   * Recovers in place, without discarding the document.
   *
   * When a caller can re-run whatever failed - the session bootstrap, a screen's
   * reads - it passes that here and the primary action becomes a retry. Only the
   * callers that genuinely cannot recover in place fall back to reloading the
   * page, which is a much blunter instrument: it discards every other screen's
   * cached data, the scroll position and the filter state the URL does not carry,
   * and it costs a full application boot to recover from what is often one failed
   * request.
   */
  readonly onRetry?: (() => void) | undefined;
  /** Where this boundary sits, recorded with the failure (see {@link reportError}). */
  readonly boundary?: string | undefined;
}

export function ErrorContent({
  error,
  onRetry,
  boundary = 'error-boundary',
}: ErrorContentProps): React.JSX.Element {
  const { t } = useTranslation();
  const code = toErrorCode(error);
  const presentation = PRESENTATION[code];
  const correlationId = correlationIdOf(error);
  const detail = describeError(error);
  const isHard = code === 'internal' || code === 'not_found' || code === 'forbidden';

  // Make `error.internal.description`'s "The issue has been logged" true. Without
  // this the generic screen was the only trace a failure left anywhere, which is
  // why an `internal` here could not be told apart from a render-time exception.
  useEffect(() => {
    reportError(error, { boundary });
  }, [error, boundary]);

  return (
    <div className="flex min-h-full items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Alert variant={isHard ? 'destructive' : 'default'}>
          <AlertTitle>{t(presentation.titleKey)}</AlertTitle>
          <AlertDescription>
            <span>{t(presentation.descriptionKey)}</span>
            {correlationId ? (
              <span className="text-muted-foreground mt-2 block text-[11px]">
                {t('error.reference', { correlationId })}
              </span>
            ) : null}
            {/* Development builds only - see `describeError` for why never in
                production. Makes a screenshot of this screen a usable bug report. */}
            {import.meta.env.DEV && detail ? (
              <pre className="text-muted-foreground mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-black/[0.04] p-2 text-left text-[10.5px] leading-[1.45]">
                {detail}
              </pre>
            ) : null}
          </AlertDescription>
        </Alert>
        <div className="mt-4 flex gap-2">
          {code === 'unauthenticated' ? (
            <Button onClick={beginLogin}>{t('error.signIn')}</Button>
          ) : (
            <>
              {onRetry ? <Button onClick={onRetry}>{t('common.retry')}</Button> : null}
              <Button variant="outline" onClick={() => window.location.reload()}>
                {t('common.reload')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
